import { useMemo } from "react";
import { useGetExistingAccountPaymentPrefsQuery } from "../../../../graphql/supergraph.index";

/**
 * A hook that manages data fetching and state for editing payment preferences at the account level.
 * It fetches existing payment preferences for an account and finds the specific payment preference
 * to be edited based on the sourcePaymentPrefId.
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.accountId - ID of the account to fetch payment preferences for
 * @param {string} [params.sourcePaymentPrefId] - ID of the payment preference to edit
 *
 * @returns {Object} Hook return object
 * @returns {boolean} returns.dataLoading - Whether account payment preferences are being loaded
 * @returns {boolean} returns.dataError - Whether an error occurred during data fetching
 * @returns {RedactedPaymentPref | undefined} returns.existingPaymentPref - The payment preference to edit
 * @returns {string | undefined} returns.accountName - Name of the account (required for GP flows with staff approvals)
 *
 * @example
 * ```tsx
 * const {
 *   dataLoading,
 *   dataError,
 *   existingPaymentPref,
 *   accountName
 * } = usePaymentPrefEditForAccountWorkflowController({
 *   accountId: "123",
 *   sourcePaymentPrefId: "456"
 * });
 * ```
 *
 * @remarks
 * - useGetExistingAccountPaymentPrefsQuery for data fetching
 * - Provides loading and error states for UI feedback
 * - Returns account name for staff approval workflows
 */
export const usePaymentPrefEditForAccountWorkflowController = ({
  accountId,
  sourcePaymentPrefId,
}: {
  accountId: string;
  sourcePaymentPrefId?: string;
}) => {
  /**
   * Fetch the existing payment prefs on account
   * Used to display the available payment prefs,
   * after filtering by supported payments.
   */
  const {
    data: existingAccountPaymentPrefsData,
    loading: existingAccountPaymentPrefsLoading,
    error: existingAccountPaymentPrefsError,
  } = useGetExistingAccountPaymentPrefsQuery({
    variables: {
      accountId,
    },
  });

  const existingPaymentPref = useMemo(
    () =>
      existingAccountPaymentPrefsData?.account?.paymentPrefs?.find(
        (paymentPref) => paymentPref.id === sourcePaymentPrefId,
      ),
    [existingAccountPaymentPrefsData?.account?.paymentPrefs, sourcePaymentPrefId],
  );

  return {
    /**
     * Combining all the loading states into one
     */
    dataLoading: existingAccountPaymentPrefsLoading,

    /**
     * Combining all the error states into one
     */
    dataError: existingAccountPaymentPrefsError,

    /**
     * Payment pref to edit
     */
    existingPaymentPref,

    /**
     * Required only on GP flows and
     * has_position_payment_pref_staff_approvals arena FF is enabled
     */
    accountName: existingAccountPaymentPrefsData?.account?.name,
  };
};
